import React, { useState, useRef, useEffect, useCallback } from 'react';
import { personalInfo, certificationsData } from '../data/mock';
import { Terminal as TerminalIcon } from 'lucide-react';

const PROMPT = 'vishal@digital-verse:~$ ';

const COMMANDS = {
  help: () => [
    { type: 'header', text: '╔══════════════════════════════════════════════════════╗' },
    { type: 'header', text: '║        DIGITAL-VERSE COMMAND CENTER v2.0             ║' },
    { type: 'header', text: '╚══════════════════════════════════════════════════════╝' },
    { type: 'blank' },
    { type: 'label', text: '  Available Commands:' },
    { type: 'blank' },
    { type: 'cmd', cmd: 'help', desc: 'Display this help menu' },
    { type: 'cmd', cmd: 'mission', desc: 'View mission statement' },
    { type: 'cmd', cmd: 'expertise', desc: 'Technical & strategic capabilities' },
    { type: 'cmd', cmd: 'zero-trust', desc: 'Zero-Trust security framework' },
    { type: 'cmd', cmd: 'ai', desc: 'AI-driven defense & threat intelligence' },
    { type: 'cmd', cmd: 'certifications', desc: 'Professional credentials & certs' },
    { type: 'cmd', cmd: 'cert_stats', desc: 'Certification analytics & coverage' },
    { type: 'cmd', cmd: 'contact', desc: 'Professional contact information' },
    { type: 'cmd', cmd: 'social', desc: 'Social media & web presence' },
    { type: 'cmd', cmd: 'cat /specializations', desc: 'Core domain specializations' },
    { type: 'cmd', cmd: 'uptime --professional', desc: 'Career stats & track record' },
    { type: 'cmd', cmd: 'clear', desc: 'Clear terminal' },
    { type: 'blank' },
    { type: 'muted', text: '  Type any command and press Enter.' },
  ],

  mission: () => [
    { type: 'blank' },
    { type: 'accent', text: '  ◆ MISSION STATEMENT' },
    { type: 'divider' },
    { type: 'green', text: '  "Securing the Digital Frontier with Zero-Trust AI"' },
    { type: 'blank' },
    { type: 'text', text: '  Build cyber-resilient, future-ready organizations capable of' },
    { type: 'text', text: '  defending and adapting in a rapidly evolving threat landscape.' },
    { type: 'blank' },
    { type: 'muted', text: '  Operating Model: Policy → Enforcement → Evidence' },
    { type: 'muted', text: '  Auditable controls · Measurable risk reduction · Safe AI adoption' },
  ],

  expertise: () => [
    { type: 'blank' },
    { type: 'accent', text: '  ◆ TECHNICAL & STRATEGIC CAPABILITIES' },
    { type: 'divider' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Executive Security Leadership' },
    { type: 'text', text: '  │  End-to-end Cybersecurity & InfoSec mandate' },
    { type: 'text', text: '  │  Board-level reporting & regulator-ready documentation' },
    { type: 'text', text: '  │  Enterprise risk programs & crisis management' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Offensive & Defensive Mastery' },
    { type: 'text', text: '  │  Red/Blue/Purple teaming · BAS validations' },
    { type: 'text', text: '  │  DFIR · CTI/Hunt · Detection engineering' },
    { type: 'text', text: '  │  SIEM/XDR content tuning · SOAR playbooks' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Architecture & Engineering' },
    { type: 'text', text: '  │  Zero-Trust (identity, device, network, app, data)' },
    { type: 'text', text: '  │  NGFW/WAF/Proxy/LB/CDN · DDoS · SSL orchestration' },
    { type: 'text', text: '  │  Multi-cloud (Azure/AWS/GCP) · CSPM/CNAPP · IaC' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Data, Identity & AI Governance' },
    { type: 'text', text: '  │  DLP/DSPM · PIM/PAM · Conditional Access · MDM/BYOD' },
    { type: 'text', text: '  │  Zero-Trust AI · Enterprise AI governance' },
    { type: 'text', text: '  │  Prompt/data-loss controls · Per-prompt audit trails' },
  ],

  'zero-trust': () => [
    { type: 'blank' },
    { type: 'accent', text: '  ◆ ZERO-TRUST SECURITY FRAMEWORK' },
    { type: 'divider' },
    { type: 'blank' },
    { type: 'green', text: '  "Never trust, always verify — across every layer."' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Five Pillars of Zero-Trust Implementation' },
    { type: 'blank' },
    { type: 'bar', label: 'Identity', value: 97 },
    { type: 'text', text: '  │  PIM/PAM · Conditional Access · JIT/JEA · MFA everywhere' },
    { type: 'blank' },
    { type: 'bar', label: 'Devices', value: 94 },
    { type: 'text', text: '  │  MDM/BYOD · Enterprise browser · Compliance posture' },
    { type: 'blank' },
    { type: 'bar', label: 'Network', value: 96 },
    { type: 'text', text: '  │  Micro-segmentation · NGFW · Deception for crown-jewels' },
    { type: 'blank' },
    { type: 'bar', label: 'Application', value: 93 },
    { type: 'text', text: '  │  AppSec · WAF · API security · Secure SDLC' },
    { type: 'blank' },
    { type: 'bar', label: 'Data', value: 95 },
    { type: 'text', text: '  │  DLP/DSPM · Sensitivity labels · Encryption at rest/transit' },
  ],

  ai: () => [
    { type: 'blank' },
    { type: 'accent', text: '  ◆ AI-DRIVEN DEFENSE & THREAT INTELLIGENCE' },
    { type: 'divider' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Zero-Trust AI Governance' },
    { type: 'text', text: '  │  Allow-listed enterprise AI (M365 Copilot, etc.)' },
    { type: 'text', text: '  │  Prompt injection prevention & data-loss controls' },
    { type: 'text', text: '  │  Content filters · Data residency & lineage' },
    { type: 'text', text: '  │  Per-prompt audit trails · Human-in-the-loop for sensitive actions' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ AI-Powered SecOps' },
    { type: 'text', text: '  │  ML-based anomaly detection across XDR/SIEM telemetry' },
    { type: 'text', text: '  │  Automated threat hunting & detection engineering' },
    { type: 'text', text: '  │  SOAR-driven incident orchestration & auto-remediation' },
    { type: 'text', text: '  │  Predictive risk scoring & vulnerability prioritization' },
    { type: 'blank' },
    { type: 'label', text: '  ┌─ Continuous Improvement' },
    { type: 'text', text: '  │  BAS validations to test AI detection fidelity' },
    { type: 'text', text: '  │  Purple-team feedback loops for model refinement' },
    { type: 'text', text: '  │  Measurable MTTD/MTTR reduction through AI automation' },
  ],

  certifications: () => {
    const lines = [
      { type: 'blank' },
      { type: 'accent', text: '  ◆ PROFESSIONAL CERTIFICATIONS' },
      { type: 'divider' },
      { type: 'blank' },
    ];
    certificationsData.forEach((cat) => {
      lines.push({ type: 'label', text: `  ┌─ ${cat.category}` });
      cat.certs.forEach((c) => {
        const extra = c.credId ? ` [${c.credId}]` : '';
        const date = c.date ? ` · ${c.date}` : '';
        lines.push({ type: 'text', text: `  │  ✓ ${c.name}` });
        lines.push({ type: 'muted', text: `  │    ${c.issuer}${date}${extra}` });
      });
      lines.push({ type: 'blank' });
    });
    return lines;
  },

  cert_stats: () => {
    const total = certificationsData.reduce((s, c) => s + c.certs.length, 0);
    const issuers = new Set();
    certificationsData.forEach((c) => c.certs.forEach((cert) => issuers.add(cert.issuer)));
    return [
      { type: 'blank' },
      { type: 'accent', text: '  ◆ CERTIFICATION ANALYTICS' },
      { type: 'divider' },
      { type: 'blank' },
      { type: 'stat', label: 'Total Credentials', value: `${total}` },
      { type: 'stat', label: 'Certification Bodies', value: `${issuers.size}` },
      { type: 'stat', label: 'Domains Covered', value: `${certificationsData.length}` },
      { type: 'blank' },
      { type: 'label', text: '  ┌─ Domain Coverage' },
      ...certificationsData.map((c) => ({
        type: 'bar', label: c.category, value: Math.round((c.certs.length / total) * 100),
      })),
      { type: 'blank' },
      { type: 'label', text: '  ┌─ Issuing Authorities' },
      ...Array.from(issuers).map((i) => ({ type: 'text', text: `  │  ▸ ${i}` })),
    ];
  },

  'cat /specializations': () => [
    { type: 'blank' },
    { type: 'green', text: '  zero-trust | ai-defense | cloud-security | enterprise-resilience' },
  ],

  'uptime --professional': () => [
    { type: 'blank' },
    { type: 'green', text: '  11+ years | 20+ enterprises secured | mission: future-ready security' },
  ],

  contact: () => [
    { type: 'blank' },
    { type: 'accent', text: '  ◆ CONTACT INFORMATION' },
    { type: 'divider' },
    { type: 'blank' },
    { type: 'stat', label: 'Email', value: personalInfo.email },
    { type: 'stat', label: 'Location', value: personalInfo.location },
    { type: 'stat', label: 'Website', value: 'vishalmajithia.com' },
    { type: 'blank' },
    { type: 'muted', text: '  Open to consulting, speaking, and advisory engagements.' },
  ],

  social: () => {
    const links = personalInfo.socialLinks;
    return [
      { type: 'blank' },
      { type: 'accent', text: '  ◆ SOCIAL & WEB PRESENCE' },
      { type: 'divider' },
      { type: 'blank' },
      { type: 'link', label: 'LinkedIn', url: links.linkedin },
      { type: 'link', label: 'GitHub', url: links.github },
      { type: 'link', label: 'X (Twitter)', url: links.x },
      { type: 'link', label: 'YouTube', url: links.youtube },
      { type: 'link', label: 'Instagram', url: links.instagram },
      { type: 'link', label: 'Facebook', url: links.facebook },
      { type: 'link', label: 'Telegram', url: links.telegram },
      { type: 'link', label: 'Discord', url: links.discord },
      { type: 'link', label: 'CyberWithVishal', url: links.cyberWithVishal },
      { type: 'link', label: 'Saraswati Repo', url: links.saraswatiRepo },
      { type: 'link', label: 'Linktree', url: links.linktree },
    ];
  },
};

const BOOT_LINES = [
  { type: 'prompt', cmd: 'whoami' },
  { type: 'green', text: '  Cybersecurity Leader | Zero-Trust & AI Strategist' },
  { type: 'blank' },
  { type: 'prompt', cmd: 'mission' },
  { type: 'accent', text: '  Securing the Digital Frontier with Zero-Trust AI' },
  { type: 'blank' },
  { type: 'prompt', cmd: 'help' },
  { type: 'muted', text: "  Type 'help' to see available commands" },
];

function TerminalLine({ line }) {
  if (line.type === 'blank') return <div className="h-3" />;
  if (line.type === 'divider') return <div className="text-[#1e3a4a] text-xs leading-none select-none">  ─────────────────────────────────────────────</div>;

  if (line.type === 'prompt') {
    return (
      <div className="flex flex-wrap">
        <span className="text-[#00e5ff]">{PROMPT}</span>
        <span className="text-[#f1f5f9]">{line.cmd}</span>
      </div>
    );
  }
  if (line.type === 'header') return <div className="text-[#00e5ff]/70 select-none">{line.text}</div>;
  if (line.type === 'label') return <div className="text-[#00e5ff] font-semibold">{line.text}</div>;
  if (line.type === 'accent') return <div className="text-[#00e5ff] font-bold">{line.text}</div>;
  if (line.type === 'green') return <div className="text-[#00ff88]">{line.text}</div>;
  if (line.type === 'muted') return <div className="text-[#4a6070]">{line.text}</div>;
  if (line.type === 'text') return <div className="text-[#94a3b8]">{line.text}</div>;

  if (line.type === 'cmd') {
    return (
      <div className="flex gap-2">
        <span className="text-[#00ff88] w-[160px] flex-shrink-0 text-right pr-2">{line.cmd}</span>
        <span className="text-[#64748b]">—</span>
        <span className="text-[#94a3b8]">{line.desc}</span>
      </div>
    );
  }

  if (line.type === 'stat') {
    return (
      <div className="flex gap-2 ml-2">
        <span className="text-[#64748b] w-[180px] flex-shrink-0 text-right pr-2">{line.label}:</span>
        <span className="text-[#00e5ff] font-semibold">{line.value}</span>
      </div>
    );
  }

  if (line.type === 'link') {
    return (
      <div className="flex gap-2 ml-2">
        <span className="text-[#00ff88] w-[160px] flex-shrink-0 text-right pr-2">▸ {line.label}</span>
        <a href={line.url} target="_blank" rel="noopener noreferrer" className="text-[#4a8ea8] hover:text-[#00e5ff] transition-colors duration-200 underline underline-offset-2 decoration-[#1e3a4a]">
          {line.url}
        </a>
      </div>
    );
  }

  if (line.type === 'bar') {
    const filled = Math.round(line.value / 5);
    const empty = 20 - filled;
    return (
      <div className="flex items-center gap-2 ml-2">
        <span className="text-[#94a3b8] w-[160px] flex-shrink-0 text-right pr-2">{line.label}</span>
        <span className="text-[#0a2a35]">[</span>
        <span className="text-[#00e5ff]">{'█'.repeat(filled)}</span>
        <span className="text-[#1a2e38]">{'░'.repeat(empty)}</span>
        <span className="text-[#0a2a35]">]</span>
        <span className="text-[#00ff88] text-[11px] ml-1">{line.value}%</span>
      </div>
    );
  }

  return <div className="text-[#94a3b8]">{line.text || ''}</div>;
}

export default function TerminalSection() {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [bootIdx, setBootIdx] = useState(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Boot sequence
  useEffect(() => {
    if (bootIdx < BOOT_LINES.length) {
      const delay = BOOT_LINES[bootIdx].type === 'prompt' ? 400 : 150;
      const timer = setTimeout(() => {
        setHistory((prev) => [...prev, BOOT_LINES[bootIdx]]);
        setBootIdx((i) => i + 1);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setIsTyping(false);
    }
  }, [bootIdx]);

  // Auto scroll - only inside terminal container, never page scroll
  const userInteracted = useRef(false);
  useEffect(() => {
    if (scrollRef.current && userInteracted.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = useCallback((cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    const newLines = [{ type: 'prompt', cmd: cmd.trim() }];

    if (trimmed === 'clear') {
      setHistory([]);
      return;
    }

    const handler = COMMANDS[trimmed];
    if (handler) {
      newLines.push(...handler());
    } else if (trimmed === '') {
      // just add prompt
    } else {
      newLines.push({ type: 'blank' });
      newLines.push({ type: 'muted', text: `  Command not found: '${cmd.trim()}'` });
      newLines.push({ type: 'muted', text: "  Type 'help' for available commands." });
    }
    newLines.push({ type: 'blank' });
    setHistory((prev) => [...prev, ...newLines]);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isTyping) return;
    userInteracted.current = true;
    executeCommand(input);
    setInput('');
  };

  const handleTerminalClick = () => {
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  };

  return (
    <section className="relative py-8 md:py-12">
      <div className="max-w-[1000px] mx-auto px-4 md:px-6">
        {/* Terminal container */}
        <div
          className="relative rounded-xl overflow-hidden border border-[#0e2a35] bg-[#080f14] shadow-[0_0_60px_rgba(0,229,255,0.06)]"
          onClick={handleTerminalClick}
          style={{ cursor: 'text' }}
        >
          {/* Ambient pulse */}
          <div className="absolute inset-0 rounded-xl opacity-30 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(0,229,255,0.04) 0%, transparent 70%)',
            animation: 'terminalPulse 6s ease-in-out infinite',
          }} />

          {/* Scanning line */}
          <div className="absolute left-0 right-0 h-[1px] pointer-events-none z-10 opacity-20" style={{
            background: 'linear-gradient(90deg, transparent, rgba(0,229,255,0.5), transparent)',
            animation: 'scanDown 8s linear infinite',
          }} />

          {/* Title bar */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#0e2a35] bg-[#060c10]">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]/70" />
              <div className="w-3 h-3 rounded-full bg-[#febc2e]/70" />
              <div className="w-3 h-3 rounded-full bg-[#28c840]/70" />
            </div>
            <div className="flex items-center gap-2 ml-2">
              <TerminalIcon className="w-3.5 h-3.5 text-[#00e5ff]/60" />
              <span className="text-[11px] text-[#4a6070] font-['JetBrains_Mono'] tracking-wider">
                digital-verse — vishal@cyber-command
              </span>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
              <span className="text-[9px] text-[#00ff88]/70 font-['JetBrains_Mono'] tracking-wider uppercase">secure</span>
            </div>
          </div>

          {/* Terminal body */}
          <div
            ref={scrollRef}
            className="p-4 md:p-5 font-['JetBrains_Mono'] text-[12px] md:text-[13px] leading-[1.7] overflow-y-auto overflow-x-hidden"
            style={{ maxHeight: '420px', minHeight: '300px' }}
          >
            {/* System boot header */}
            <div className="text-[#1a3a4a] text-[10px] mb-3 select-none">
              ▸ Digital-Verse Cyber Command Center v2.0 — Initialized
            </div>

            {/* History lines */}
            {history.map((line, i) => (
              <TerminalLine key={i} line={line} />
            ))}

            {/* Input line */}
            {!isTyping && (
              <form onSubmit={handleSubmit} className="flex flex-wrap items-center mt-1">
                <span className="text-[#00e5ff] flex-shrink-0">{PROMPT}</span>
                <div className="relative flex-1 min-w-[100px]">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="w-full bg-transparent text-[#f1f5f9] outline-none caret-transparent font-['JetBrains_Mono'] text-[12px] md:text-[13px]"
                    autoFocus={false}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  {/* Custom cursor */}
                  <span
                    className="absolute top-0 h-full pointer-events-none"
                    style={{ left: `${input.length * 7.8}px` }}
                  >
                    <span className="inline-block w-[8px] h-[15px] bg-[#00e5ff] mt-[2px]" style={{
                      animation: 'terminalBlink 1s step-end infinite',
                    }} />
                  </span>
                </div>
              </form>
            )}

            {/* Typing cursor during boot */}
            {isTyping && (
              <div className="flex items-center">
                <span className="text-[#00e5ff]">{PROMPT}</span>
                <span className="inline-block w-[8px] h-[15px] bg-[#00e5ff]" style={{
                  animation: 'terminalBlink 1s step-end infinite',
                }} />
              </div>
            )}
          </div>

          {/* Bottom bar */}
          <div className="flex items-center justify-between px-4 py-1.5 border-t border-[#0e2a35] bg-[#060c10]">
            <span className="text-[9px] text-[#1a3a4a] font-['JetBrains_Mono']">
              TLS 1.3 · AES-256 · Zero-Trust Verified
            </span>
            <span className="text-[9px] text-[#1a3a4a] font-['JetBrains_Mono']">
              {history.filter((l) => l.type === 'prompt').length} commands executed
            </span>
          </div>
        </div>

        {/* Hint below terminal */}
        <p className="text-center text-[10px] text-[#2a4050] font-['JetBrains_Mono'] mt-3 tracking-wider">
          Click terminal and type a command · Try: help, expertise, zero-trust, ai, certifications
        </p>
      </div>

      <style>{`
        @keyframes terminalBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes terminalPulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.4; }
        }
        @keyframes scanDown {
          0% { top: 0; }
          100% { top: 100%; }
        }
      `}</style>
    </section>
  );
}
